import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Upload, AlertCircle, HelpCircle, Check, X
} from 'lucide-react';

// Country list for country field type
const COUNTRIES = [
  'Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Ethiopia', 'Nigeria', 'South Africa',
  'Ghana', 'Egypt', 'Morocco', 'USA', 'UK', 'Canada', 'Germany', 'France', 'India',
  'China', 'Japan', 'Australia', 'Brazil', 'Argentina', 'Mexico', 'Spain', 'Italy',
  'Netherlands', 'Belgium', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Ireland',
  'Portugal', 'Switzerland', 'Austria', 'Poland', 'Czech Republic', 'Hungary',
  'Greece', 'Turkey', 'Saudi Arabia', 'UAE', 'Qatar', 'Kuwait', 'Oman', 'Bahrain',
  'Malaysia', 'Singapore', 'Indonesia', 'Thailand', 'Philippines', 'Vietnam',
  'South Korea', 'New Zealand', 'Other'
];

// Evaluate conditional visibility rules
const evaluateConditions = (conditionalRules, formValues) => {
  if (!conditionalRules || !conditionalRules.show_when || conditionalRules.show_when.length === 0) {
    return true; // No conditions means always visible
  }

  // Check all conditions (AND logic)
  return conditionalRules.show_when.every(condition => {
    const fieldValue = formValues[condition.field];
    
    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'not_equals':
        return fieldValue !== condition.value;
      case 'contains':
        return fieldValue && fieldValue.toLowerCase().includes(condition.value.toLowerCase());
      case 'not_empty':
        return fieldValue && fieldValue.toString().trim() !== '';
      default:
        return true;
    }
  });
};

// Single field renderer
const DynamicField = ({ 
  field, 
  value, 
  onChange, 
  error, 
  disabled = false,
  onFileUpload
}) => {
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const handleChange = (e) => {
    onChange(field.field_name, e.target.value);
  };

  const handleCheckboxChange = (e) => {
    onChange(field.field_name, e.target.checked);
  };

  const handleMultiSelectChange = (optionValue) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter(v => v !== optionValue)
      : [...currentValues, optionValue];
    onChange(field.field_name, newValues);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Allowed: JPG, PNG, GIF, PDF, DOC, DOCX');
      return;
    }

    setUploadError(null);
    setUploadProgress(0);

    try {
      // If there's an onFileUpload callback, use it for actual upload
      if (onFileUpload) {
        const result = await onFileUpload(file, (progress) => {
          setUploadProgress(progress);
        });
        onChange(field.field_name, result);
      } else {
        // Fallback: store file reference locally
        onChange(field.field_name, {
          name: file.name,
          size: file.size,
          type: file.type,
          file: file // Store the actual file for later upload
        });
      }
      setUploadProgress(100);
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
      setUploadProgress(null);
    }
  };

  const baseInputClass = `w-full border rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 
    focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors
    ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`;

  const renderField = () => {
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            className={baseInputClass}
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={disabled}
            rows={4}
            className={baseInputClass}
            data-testid={`field-${field.field_name}`}
          />
        );

      case 'select':
        const options = field.field_options || [];
        return (
          <select
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            className={baseInputClass}
            data-testid={`field-${field.field_name}`}
          >
            <option value="">{field.placeholder || 'Select an option...'}</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'multiselect':
        const multiOptions = field.field_options || [];
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {multiOptions.map((opt, idx) => (
              <label 
                key={idx} 
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.value)}
                  onChange={() => handleMultiSelectChange(opt.value)}
                  disabled={disabled}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <span className="text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'country':
        return (
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={value || ''}
              onChange={handleChange}
              disabled={disabled}
              className={`${baseInputClass} pl-10`}
              data-testid={`field-${field.field_name}`}
            >
              <option value="">Select a country...</option>
              {COUNTRIES.map((country, idx) => (
                <option key={idx} value={country}>{country}</option>
              ))}
            </select>
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={handleCheckboxChange}
              disabled={disabled}
              className="w-5 h-5 text-green-600 rounded"
              data-testid={`field-${field.field_name}`}
            />
            <span className="text-gray-700">{field.placeholder || 'Yes'}</span>
          </label>
        );

      case 'file':
        return (
          <div className="space-y-2">
            <div className={`border-2 border-dashed rounded-lg p-6 text-center 
              ${error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-green-400'}
              ${disabled ? 'bg-gray-100' : 'bg-white'}`}
            >
              {value ? (
                <div className="flex items-center justify-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{value.name || 'File uploaded'}</span>
                  <button
                    type="button"
                    onClick={() => onChange(field.field_name, null)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop or click to upload
                  </p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    disabled={disabled}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                    data-testid={`field-${field.field_name}`}
                  />
                  <p className="text-xs text-gray-400">
                    {field.help_text || 'JPG, PNG, GIF, PDF, DOC (max 5MB)'}
                  </p>
                </>
              )}
            </div>
            
            {uploadProgress !== null && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
            
            {uploadError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {uploadError}
              </p>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        );
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {field.field_label}
        {field.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {renderField()}
      
      {field.help_text && field.field_type !== 'file' && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <HelpCircle className="w-3 h-3" /> {field.help_text}
        </p>
      )}
      
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" /> {error}
        </p>
      )}
    </div>
  );
};

// Main Form Renderer Component
const DynamicFormRenderer = ({
  fields = [],
  values = {},
  onChange,
  errors = {},
  disabled = false,
  onFileUpload,
  className = ''
}) => {
  // Filter visible fields based on conditional rules
  const visibleFields = fields.filter(field => 
    evaluateConditions(field.conditional_rules, values)
  );

  const handleFieldChange = useCallback((fieldName, value) => {
    onChange({
      ...values,
      [fieldName]: value
    });
  }, [values, onChange]);

  // Sort fields by sort_order
  const sortedFields = [...visibleFields].sort((a, b) => 
    (a.sort_order || 0) - (b.sort_order || 0)
  );

  return (
    <div className={`space-y-6 ${className}`} data-testid="dynamic-form-renderer">
      {sortedFields.map((field) => (
        <DynamicField
          key={field.field_name || field.id}
          field={field}
          value={values[field.field_name]}
          onChange={handleFieldChange}
          error={errors[field.field_name]}
          disabled={disabled}
          onFileUpload={onFileUpload}
        />
      ))}
      
      {sortedFields.length === 0 && (
        <p className="text-gray-500 text-center py-4">
          No registration fields configured.
        </p>
      )}
    </div>
  );
};

// Validation helper
export const validateFormFields = (fields, values) => {
  const errors = {};
  
  fields.forEach(field => {
    // Skip hidden fields (due to conditions)
    if (!evaluateConditions(field.conditional_rules, values)) {
      return;
    }
    
    const value = values[field.field_name];
    
    // Required validation
    if (field.is_required) {
      if (value === undefined || value === null || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        errors[field.field_name] = `${field.field_label} is required`;
        return;
      }
    }
    
    // Type-specific validation
    if (value) {
      switch (field.field_type) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors[field.field_name] = 'Please enter a valid email address';
          }
          break;
        case 'phone':
          // Basic phone validation (allow digits, spaces, dashes, plus)
          const phoneRegex = /^[\d\s\-+()]{7,20}$/;
          if (!phoneRegex.test(value)) {
            errors[field.field_name] = 'Please enter a valid phone number';
          }
          break;
        case 'number':
          if (isNaN(Number(value))) {
            errors[field.field_name] = 'Please enter a valid number';
          }
          break;
        default:
          break;
      }
    }
  });
  
  return errors;
};

export default DynamicFormRenderer;
export { DynamicField, evaluateConditions, COUNTRIES };
